// Bulk-ordering keyword cluster (September 2026).
//
// Semrush returned no volume for "palworld tcg bulk" and its variants, so the
// demand here is UNKNOWN rather than zero — the cluster is built because the
// intent is real and unserved on our own site: /tcg-bulk-order shipped without
// any supporting text targeting "palworld tcg bulk order", "palworld bulk
// cards", "palworld tcg bulk prices" or "palworld tcg ordering". Treat the
// keyword list as a coverage bet, not a validated volume claim.
//
// Structure copies src/lib/guides-shirts-shifts-2026.ts: five AEO-shaped
// articles per topic (overview, step-by-step, reference, mistakes, FAQ). No
// invented prices, discounts, print runs or shipping terms.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

interface Topic {
  slug: string;
  title: string;
  desc: string;
  keywords: string;
  hub: string;
  hubLabel: string;
  tag: string;
  question: string;
  answer: string;
  steps: [string, string][];
  data: [string, string][];
  pitfalls: [string, string][];
  faq: [string, string][];
}

const TOPICS: Topic[] = [
  {
    slug: "palworld-tcg-bulk-order-guide",
    title: "Palworld TCG Bulk Order Guide",
    desc: "How to bulk order Palworld TCG singles: build a quantity-plus-card-number list, import a CSV deck list, preview every match and hand the whole cart to a single TCGplayer checkout.",
    keywords:
      "palworld tcg bulk order, palworld tcg ordering, palworld bulk cards, palworld tcg bulk, palworld card bulk order, order palworld cards in bulk, palworld tcg mass entry",
    hub: "/tcg-bulk-order",
    hubLabel: "Palworld TCG bulk order builder",
    tag: "TCG",
    question: "How do you place a bulk order for Palworld TCG cards?",
    answer:
      "A Palworld TCG bulk order is a quantity-plus-card-number list rather than a series of individual searches: you write one line per printing, match each line against the official English printing list, then hand the finished list to TCGplayer's Mass Entry checkout so everything lands in one cart. The matching step is what makes or breaks the order, because printed Palworld codes carry a leading E and parallel rarities share a base number, so an unmatched or mistyped line drops silently instead of erroring.",
    steps: [
      [
        "Write one line per printing, not per card name",
        "Use the format quantity then card number, one per line. A card name alone is ambiguous once a printing has a parallel, so the number is what identifies what you are buying.",
      ],
      [
        "Use the exact printed code, including the leading E",
        "Palworld English codes look like EBP01-001 or ETD01-012TSP. Dropping the E or the rarity suffix is the single most common reason a bulk line fails to match.",
      ],
      [
        "Import instead of retyping when you already have a list",
        "A deck list exported as CSV or plain text can be dropped straight in, so an existing decklist becomes an order without being transcribed by hand.",
      ],
      [
        "Search the database for anything you only know by name",
        "If you remember the Pal but not the number, search the card database from the builder and add the printing you want — it appends the correctly formatted line for you.",
      ],
      [
        "Read the preview before you send anything",
        "The preview shows the matched card name, set and quantity for every line, and lists the lines it could not match. Fix those before continuing, or you will pay for a short order.",
      ],
      [
        "Hand off to one TCGplayer checkout",
        "The builder opens TCGplayer's Mass Entry with the whole list attached, so seller selection, shipping and payment all happen once on TCGplayer rather than per card.",
      ],
    ],
    data: [
      ["Input format", "Quantity followed by the printed card number, one printing per line."],
      ["Code format", "Official English codes keep the leading E: EBP01-001, ETD01-012TSP, ESOUL-002."],
      ["File import", "CSV or plain-text lists are accepted and parsed into order lines."],
      ["Matching", "Every line is checked against the official printing list before checkout."],
      ["Preview", "Matched name, set, code and quantity per line, with unmatched lines flagged separately."],
      ["Checkout", "TCGplayer Mass Entry — one cart, one shipping decision, one payment."],
      ["Pricing", "Per-card cost is resolved by TCGplayer at checkout, not fixed by the list."],
    ],
    pitfalls: [
      [
        "Pasting card names instead of numbers",
        "Names collide across parallels and reprints. Paste numbers, or add cards from the database search so the number is filled in for you.",
      ],
      [
        "Dropping the leading E from the code",
        "BP01-001 is not the printed English code. Use EBP01-001 or the line will not match.",
      ],
      [
        "Ignoring the unmatched-lines warning",
        "Unmatched lines simply do not travel to checkout. A 40-line order can quietly become a 34-line order if the warning is skipped.",
      ],
      [
        "Assuming one seller covers the whole list",
        "A large list is usually filled from several sellers, which changes shipping. Check the cart on TCGplayer before confirming.",
      ],
    ],
    faq: [
      [
        "How do I bulk order Palworld TCG cards?",
        "Build a list of quantity plus card number, one printing per line, preview the matches, then send the list to TCGplayer's Mass Entry checkout so all the singles are added to one cart.",
      ],
      [
        "Can I import a CSV deck list for a Palworld bulk order?",
        "Yes. A CSV or plain-text list can be dropped into the builder and is parsed into order lines, so an exported decklist does not have to be retyped.",
      ],
      [
        "What card number format does a Palworld bulk order need?",
        "The exact printed English code with the leading E, such as EBP01-001 or ETD01-012TSP. Rarity suffixes matter because parallels share the base number.",
      ],
      [
        "What happens to a card number that is not recognised?",
        "It is listed as unmatched in the preview and excluded from checkout. Correct or remove it before ordering rather than assuming it went through.",
      ],
      [
        "Can I add cards if I only know the Pal's name?",
        "Yes — search the card database inside the builder and add the printing from the results, which appends a correctly formatted line.",
      ],
      [
        "Where does the Palworld bulk order actually check out?",
        "On TCGplayer, through its Mass Entry cart. The builder prepares and hands over the list; payment and shipping are handled entirely by TCGplayer.",
      ],
    ],
  },
  {
    slug: "palworld-tcg-bulk-prices",
    title: "Palworld TCG Bulk Prices",
    desc: "How to price a Palworld TCG bulk buy: what changes when you buy singles in quantity, which lines drive the total, and how to sanity-check a bulk quote against asking and completed-sale data.",
    keywords:
      "palworld tcg bulk prices, palworld bulk card prices, palworld bulk, palworld tcg bulk cost, cheapest way to buy palworld cards, palworld singles in bulk, palworld tcg bulk value",
    hub: "/prices",
    hubLabel: "Palworld TCG price chart",
    tag: "TCG",
    question: "How are Palworld TCG bulk prices worked out?",
    answer:
      "A Palworld bulk order is priced line by line — the total is the sum of each printing's current market price times its quantity, plus shipping — so there is no bulk discount rate to look up. What changes with quantity is which sellers can fill the whole list, and that is what moves the effective per-card cost. The way to sanity-check a quote is to compare the expensive lines against current asking prices and, where they exist, TCGplayer completed-sale averages, then decide whether to swap a parallel for its base printing.",
    steps: [
      [
        "Sort your list by line cost, not by card count",
        "Two or three chase printings usually carry most of a bulk total. Those are the only lines worth researching individually.",
      ],
      [
        "Check the asking price on the expensive lines",
        "Asking prices move first and tell you what sellers want today. That is the number your bulk quote is built from.",
      ],
      [
        "Confirm with completed-sale averages where available",
        "Completed-sale data from TCGplayer shows what buyers actually accepted, which is what distinguishes a real price level from an optimistic listing.",
      ],
      [
        "Decide parallel versus base printing per line",
        "Parallels and special rarities share a base number but not a price. Swapping to the base printing on non-key cards is the largest single saving on most bulk lists.",
      ],
      [
        "Re-preview after every substitution",
        "Changing a code changes the match. Run the preview again so you are pricing the list you are about to buy.",
      ],
      [
        "Count shipping once, not per card",
        "The reason bulk beats one-at-a-time buying is consolidated shipping. Compare totals including shipping, or the comparison is meaningless.",
      ],
    ],
    data: [
      ["Pricing model", "Per-line market price times quantity, summed — no fixed bulk discount rate."],
      ["Main cost driver", "A small number of high-value printings, not the number of lines."],
      ["Asking prices", "What sellers currently want; the figure a live quote is built from."],
      ["Completed sales", "TCGplayer completed-sale averages, where a printing has enough of them to be meaningful."],
      ["Rarity effect", "Parallels and special rarities price separately from the base printing that shares their number."],
      ["Shipping", "Consolidated once per cart, which is the structural advantage of ordering in bulk."],
      ["Volatility", "Promos and thinly traded printings move most, so their quote ages fastest."],
    ],
    pitfalls: [
      [
        "Expecting a bulk discount percentage",
        "Singles are priced per listing. Savings come from consolidated shipping and from substituting printings, not from a quantity tier.",
      ],
      [
        "Pricing a list from a remembered number",
        "Card prices move. Price the list from the live chart on the day you order, not from a figure you saw last month.",
      ],
      [
        "Treating one completed sale as a market price",
        "A thin sample is a data point, not a level. Prefer printings with enough completed sales to average before you build a total around them.",
      ],
      [
        "Buying parallels by accident",
        "A rarity suffix on a code can multiply a line's cost. Check the preview's set and rarity column before ordering.",
      ],
    ],
    faq: [
      [
        "Is there a bulk discount on Palworld TCG singles?",
        "No. Each line is priced at its own market price, so a bulk order costs the sum of its lines. The saving comes from paying shipping once instead of per card.",
      ],
      [
        "What drives the total on a Palworld bulk card order?",
        "Usually a handful of high-value printings. Sorting your list by line cost shows immediately which cards to research and which to ignore.",
      ],
      [
        "How do I check whether a bulk quote is fair?",
        "Compare the expensive lines against current asking prices, then against TCGplayer completed-sale averages where the printing has enough of them.",
      ],
      [
        "Why did the same bulk list cost more this week?",
        "Asking prices move with supply, event results and reprint news, and a bulk total is just the sum of those live prices. Re-price the list rather than reusing an old total.",
      ],
      [
        "Can I lower a bulk total without cutting cards?",
        "Often yes: swap parallels and special rarities for the base printing on cards you do not specifically want in that finish, then re-run the preview.",
      ],
      [
        "Where can I see whether the whole Palworld card market is moving?",
        "The Pal Stars TCG Price Index is an equal-weight benchmark for the category, which tells you whether a bulk total rose because of one card or because of the market.",
      ],
    ],
  },
];

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

function articles(t: Topic): GuideArticle[] {
  const hubSentence = `Everything here pairs with the ${t.hubLabel} at ${t.hub}.`;
  return [
    {
      title: t.title,
      slug: "overview",
      intro: `${t.answer} ${hubSentence}`,
      sections: [
        { heading: t.question, text: t.answer },
        { heading: "Why this matters", text: `${t.steps[0][1]} ${t.pitfalls[0][1]}` },
        { heading: "What is actually confirmed", text: t.data.map(([k, v]) => `${k}: ${v}`).join(" ") },
        {
          heading: `Use it with the ${t.hubLabel}`,
          text: `${hubSentence} Open ${t.hub} and work from the live data there rather than a remembered number.`,
        },
      ],
      externalLinks: pool(4),
    },
    {
      title: `${t.title}: step-by-step`,
      slug: "step-by-step",
      intro: "The order to work through, with the mistake each step removes.",
      sections: t.steps.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(1),
    },
    {
      title: `${t.title}: reference table`,
      slug: "reference",
      intro: `Confirmed details behind ${t.title.toLowerCase()}, in the order you will need them.`,
      sections: t.data.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(2),
    },
    {
      title: `${t.title}: mistakes and fixes`,
      slug: "mistakes",
      intro: "The failure modes that cost the most on this topic, each with the fix.",
      sections: t.pitfalls.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(5),
    },
    {
      title: `${t.title}: FAQ`,
      slug: "faq",
      intro: `Direct answers to the questions people ask about ${t.title.toLowerCase()}.`,
      sections: t.faq.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(3),
    },
  ];
}

export const BULK_ORDER_GUIDES: Guide[] = TOPICS.map((t) => ({
  slug: t.slug,
  title: `${t.title} & FAQ`,
  desc: t.desc,
  tag: t.tag,
  updated: "2026-09-13",
  keywords: t.keywords,
  articles: articles(t),
}));

/** Hub page each cluster supports, for internal-link rendering. */
export const BULK_ORDER_CLUSTERS: Record<string, { slug: string; title: string }[]> = TOPICS.reduce(
  (acc, t) => {
    (acc[t.hub] ??= []).push({ slug: t.slug, title: t.title });
    return acc;
  },
  {} as Record<string, { slug: string; title: string }[]>,
);
